/**
 * Themes and density. Both are attributes on <html> (data-theme, data-density) that swap the
 * semantic token tier; nothing else changes. They can also be set on any element to scope
 * a theme to part of the page (the design catalogue does this).
 */
import { DENSITIES, DEFAULT_DENSITY, DEFAULT_THEME, THEMES, isDensityName, isThemeName } from '@orgus/tokens';
import type { DensityName, ThemeName } from '@orgus/tokens';

export type { DensityName, ThemeName };
export { DEFAULT_DENSITY, DEFAULT_THEME, isDensityName, isThemeName };

export const THEME_STORAGE_KEY = 'fd.theme';
export const DENSITY_STORAGE_KEY = 'fd.density';

export interface ThemeOption {
  value: ThemeName;
  label: string;
}

/** The themes a person chooses between: light and dark. (A high-contrast token set still exists for forced colours; it is not offered.) */
export const THEME_OPTIONS: readonly ThemeOption[] = THEMES.filter((t) => t.name === 'dark' || t.name === 'light').map((t) => ({ value: t.name, label: t.label }));
export const DENSITY_OPTIONS: ReadonlyArray<{ value: DensityName; label: string }> = DENSITIES.map((d) => ({ value: d.name, label: d.label }));

export function themeLabel(t: ThemeName): string {
  return THEME_OPTIONS.find((o) => o.value === t)?.label ?? t;
}

/** The theme the toggle switches to: dark and light in turn. */
export function nextTheme(t: ThemeName): ThemeName {
  const i = THEME_OPTIONS.findIndex((o) => o.value === t);
  return THEME_OPTIONS[(i + 1) % THEME_OPTIONS.length]?.value ?? DEFAULT_THEME;
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readStoredTheme(): ThemeName {
  return resolveMode(readStoredMode());
}

/** What a person picked: a theme, or System (follow the computer's light or dark setting). */
export type ThemeMode = 'dark' | 'light' | 'system';
export const MODE_OPTIONS: ReadonlyArray<{ value: ThemeMode; label: string }> = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

export function readStoredMode(): ThemeMode {
  const v = storage()?.getItem(THEME_STORAGE_KEY);
  // A stored high-contrast choice from before it was retired becomes dark.
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'dark';
}

function prefersLight(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: light)').matches;
}

export function resolveMode(m: ThemeMode): ThemeName {
  return m === 'system' ? (prefersLight() ? 'light' : 'dark') : m;
}

let systemWatch: (() => void) | null = null;

/** Apply and remember a mode; System follows the computer's setting live. */
export function applyMode(m: ThemeMode): void {
  systemWatch?.();
  systemWatch = null;
  const r = root();
  if (r) r.dataset.theme = resolveMode(m);
  try {
    storage()?.setItem(THEME_STORAGE_KEY, m);
  } catch {
    // private mode: the choice lasts for this tab only
  }
  if (m === 'system' && typeof matchMedia === 'function') {
    const mq = matchMedia('(prefers-color-scheme: light)');
    const on = () => {
      const el = root();
      if (el) el.dataset.theme = mq.matches ? 'light' : 'dark';
    };
    mq.addEventListener('change', on);
    systemWatch = () => mq.removeEventListener('change', on);
  }
}

export function readStoredDensity(): DensityName {
  const v = storage()?.getItem(DENSITY_STORAGE_KEY);
  return isDensityName(v) ? v : DEFAULT_DENSITY;
}

function root(el?: HTMLElement): HTMLElement | null {
  return el ?? (typeof document === 'undefined' ? null : document.documentElement);
}

/** Apply and remember a theme. */
export function applyTheme(t: ThemeName, el?: HTMLElement): void {
  const r = root(el);
  if (r) r.dataset.theme = t;
  if (!el) {
    try {
      storage()?.setItem(THEME_STORAGE_KEY, t);
    } catch {
      // private mode: the choice lasts for this tab only
    }
  }
}

/** Apply and remember a density. */
export function applyDensity(d: DensityName, el?: HTMLElement): void {
  const r = root(el);
  if (r) r.dataset.density = d;
  if (!el) {
    try {
      storage()?.setItem(DENSITY_STORAGE_KEY, d);
    } catch {
      // private mode: the choice lasts for this tab only
    }
  }
}
